import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import { type ReactElement, type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { Amount } from '../../components/Amount';
import { MoneyOrUnavailable } from '../../components/MoneyOrUnavailable';
import { StatusChip, type StatusTone } from '../../components/StatusChip';
import { reasonMessage } from '../../i18n';
import { formatClockTime, formatClockTimeOrDate } from '../shared/format-time';

import { PositionTimeline } from './PositionTimeline';
import { formatDecimal, isSameDecimal } from './formatters';
import type { FxmatePosition } from './positions-model';

type ShownStatus = Exclude<FxmatePosition['status'], 'OPEN'>;

/**
 * Chip tone of each state of spec §4 (C3 R12: features map their states to a tone). `OPEN` has no
 * chip: mockup 7 shows none on an open position.
 */
const STATUS_TONE: Record<ShownStatus, StatusTone> = {
  OPENING: 'info',
  PARTIALLY_CLOSED: 'neutral',
  CLOSING: 'warning',
  TRACKING_LOST: 'danger',
};

/** Sizes in px drawn on mockup 7 (`docs/06-MiniApp/Maquettes/Positions.html`). */
const MOCKUP7 = {
  /** Instrument and result of an expanded card. */
  titleSize: 17,
  /** Side badge, timeline heading. */
  smallSize: 12,
  /** Detail rows, lots and channel of a collapsed card, timeline steps. */
  bodySize: 13,
  /** Card padding (14px), and 12px top and bottom when collapsed (MUI spacing unit = 8px). */
  padding: 1.75,
  collapsedPaddingY: 1.5,
} as const;

/**
 * One FXMate position of E8 (spec R1–R5, mockup 7). Collapsed, it is one line: side, instrument,
 * lots and channel, result, plus the state chip and the alerts that must be seen without opening
 * it (AC10, AC11). Expanding it shows the details (announced and obtained entry, SL with "at BE",
 * TP) and loads the timeline (R6).
 */
export function PositionCard({
  position,
  now,
}: {
  readonly position: FxmatePosition;
  /** When the list arrived: a lost position's "as of" time shows its date on another day. */
  readonly now: number | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const titleId = useId();
  const detailsId = useId();
  const lots = t('positions.lots', { lots: formatDecimal(position.lots, language, 2) });
  // One alert per distinct text: the same code twice, or two codes unknown to this version (both
  // resolve to the generic C2 text, C2 R7), never show two identical banners.
  const warnings = [
    ...new Map(
      position.warnings.map((code) => {
        const message = reasonMessage(code);
        return [message.key, message] as const;
      }),
    ).values(),
  ];

  return (
    <Paper
      component="article"
      variant="outlined"
      aria-labelledby={titleId}
      sx={{
        px: MOCKUP7.padding,
        py: expanded ? MOCKUP7.padding : MOCKUP7.collapsedPaddingY,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {/* WAI-ARIA accordion: the heading holds the button that opens the card. */}
        <Box component="h3" id={titleId} sx={{ m: 0, flex: 1, minWidth: 0, font: 'inherit' }}>
          <ButtonBase
            onClick={() => {
              setExpanded((open) => !open);
            }}
            aria-expanded={expanded}
            aria-controls={expanded ? detailsId : undefined}
            sx={{
              width: '100%',
              minHeight: 44,
              justifyContent: 'flex-start',
              gap: 1.25,
              textAlign: 'left',
              borderRadius: '8px',
            }}
          >
            <SideBadge side={position.side} />{' '}
            {expanded ? (
              <Box component="span" sx={{ fontSize: MOCKUP7.titleSize, fontWeight: 700 }}>
                {position.symbol}
              </Box>
            ) : (
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {position.symbol}
                </Box>{' '}
                <Box
                  component="span"
                  sx={{
                    fontFamily: NUMERIC_FONT,
                    fontSize: MOCKUP7.bodySize,
                    color: 'text.secondary',
                  }}
                >
                  {lots}
                </Box>{' '}
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: MOCKUP7.bodySize,
                    color: 'text.secondary',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {position.channel.title}
                </Box>
              </Box>
            )}
            <ExpandMoreOutlined
              aria-hidden
              fontSize="small"
              sx={{
                color: 'text.secondary',
                ml: expanded ? 0 : 'auto',
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
            />
          </ButtonBase>
        </Box>
        <Box sx={{ fontSize: expanded ? MOCKUP7.titleSize : undefined }}>
          <Result position={position} now={now} />
        </Box>
      </Box>

      {position.status !== 'OPEN' && (
        <Box>
          <StatusChip tone={STATUS_TONE[position.status]}>
            {t(`positions.status.${position.status}`)}
          </StatusChip>
        </Box>
      )}
      {position.status === 'TRACKING_LOST' && (
        <Alert
          severity="error"
          sx={(theme) => ({ bgcolor: theme.fx.dangerBg, color: theme.fx.text })}
        >
          {t('reason.POSITION_TRACKING_LOST')}
        </Alert>
      )}
      {/* FXM-04 §6 case 11: visible without expanding, whatever the status (spec R5, AC11). */}
      {/* An unknown code shows the generic C2 text (C2 R7), never the raw value (I12). */}
      {warnings.map((message) => (
        <Alert
          key={message.key}
          severity="warning"
          sx={(theme) => ({ bgcolor: theme.fx.warningBg, color: theme.fx.warningText })}
        >
          {t(message.key, message.values)}
        </Alert>
      ))}

      {/* Mounted only while expanded: reopening fetches the timeline again (spec R15). */}
      {expanded && (
        <Box id={detailsId} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Details position={position} lots={lots} />
          <Box
            component="h4"
            sx={{
              m: 0,
              pt: 0.5,
              fontSize: MOCKUP7.smallSize,
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {t('positions.timeline.title')}
          </Box>
          <PositionTimeline positionId={position.id} />
        </Box>
      )}
    </Paper>
  );
}

/** BUY on green, SELL on red, white text (mockup 7). */
function SideBadge({ side }: { readonly side: FxmatePosition['side'] }): ReactElement {
  const { t } = useTranslation();
  return (
    <Box
      component="span"
      sx={(theme) => ({
        flexShrink: 0,
        fontSize: MOCKUP7.smallSize,
        fontWeight: 700,
        color: theme.fx.surface,
        bgcolor: side === 'BUY' ? theme.fx.success : theme.fx.danger,
        px: 1,
        py: 0.375,
        borderRadius: '6px',
      })}
    >
      {t(`positions.side.${side}`)}
    </Box>
  );
}

/** Channel, size, announced and obtained entry, SL (with "at BE"), TP (spec R1–R3). */
function Details({
  position,
  lots,
}: {
  readonly position: FxmatePosition;
  readonly lots: string;
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const price = (value: string | null): string =>
    value === null ? t('positions.none') : formatDecimal(value, language);
  const announced =
    position.announcedEntryPrice === null
      ? t('positions.market')
      : price(position.announcedEntryPrice);
  const atBreakEven =
    position.stopLoss !== null &&
    position.entryPrice !== null &&
    isSameDecimal(position.stopLoss, position.entryPrice);
  return (
    <Box component="dl" sx={{ m: 0 }}>
      <Row label={t('positions.field.channel')}>{position.channel.title}</Row>
      <Row label={t('positions.field.size')} numeric>
        {lots}
      </Row>
      <Row label={t('positions.field.entry')} numeric>
        {`${announced} / ${price(position.entryPrice)}`}
      </Row>
      <Row label={t('positions.field.stopLoss')} numeric>
        {atBreakEven
          ? t('positions.atBreakEven', { price: price(position.stopLoss) })
          : price(position.stopLoss)}
      </Row>
      <Row label={t('positions.field.riskAtStop')}>
        <RiskAtStop position={position} />
      </Row>
      <Row label={t('positions.field.takeProfit')} numeric>
        {price(position.takeProfit)}
      </Row>
    </Box>
  );
}

/**
 * What the trader loses if the stop loss is hit now (specs/P10-risk-at-stop/spec.md R1–R5, §4),
 * computed by the API in the account currency. `stopState` decides the words: a break even reads
 * "protected" (R3), no stop reads "no stop" and never a number (R4), whatever amount came with it;
 * a risk the API could not compute reads « — » (R5), never 0.
 */
function RiskAtStop({ position }: { readonly position: FxmatePosition }): ReactElement {
  const { t } = useTranslation();
  switch (position.stopState) {
    case 'NONE':
      return <>{t('positions.risk.noStop')}</>;
    case 'PROTECTED':
      return position.riskAtStop === null ? (
        <>{t('positions.risk.protected')}</>
      ) : (
        <>
          <Amount {...position.riskAtStop} /> · {t('positions.risk.protected')}
        </>
      );
    case 'SET':
      return (
        <MoneyOrUnavailable
          value={position.riskAtStop}
          unavailableLabel={t('positions.risk.unavailable')}
        />
      );
  }
}

/**
 * Current result in the account currency (R9, Q27). Hidden while opening (spec §4). When tracking
 * is lost, FXM-04 §5 says the real state is unknown: the last result is shown only with the time
 * it was known (`resultAsOf`, C1 decision 7, provisional Q54), « au HH:MM » / "as of HH:MM",
 * with the date in front when it is not the day the list arrived (« au 13/10 10:05 »; proposal,
 * to be validated, M5 §4 and §8 "Time without a date"); without that time it is hidden, since it
 * would read as the current one. `resultAsOf` on any other status is ignored.
 */
function Result({
  position,
  now,
}: {
  readonly position: FxmatePosition;
  readonly now: number | undefined;
}): ReactElement | null {
  const { t } = useTranslation();
  const language = useLanguage();
  if (position.floatingResult === null || position.status === 'OPENING') return null;
  if (position.status !== 'TRACKING_LOST') return <Amount {...position.floatingResult} signed />;
  if (position.resultAsOf === null) return null;
  const asOf = position.resultAsOf;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Amount {...position.floatingResult} signed />
      <Box
        component="span"
        sx={{ fontSize: MOCKUP7.smallSize, color: 'text.secondary', whiteSpace: 'nowrap' }}
      >
        {/* The UTC instant stays in <time dateTime> for assistive tech (spec §4, timeline times). */}
        <time dateTime={asOf}>
          {t('positions.resultAsOf', {
            time:
              now === undefined
                ? formatClockTime(asOf, language)
                : formatClockTimeOrDate(asOf, language, { now }),
          })}
        </time>
      </Box>
    </Box>
  );
}

function Row({
  label,
  numeric,
  children,
}: {
  readonly label: string;
  readonly numeric?: boolean;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        fontSize: MOCKUP7.bodySize,
        py: 0.75,
        '&:not(:last-of-type)': { borderBottom: `1px solid ${theme.fx.neutralBg}` },
      })}
    >
      <Box component="dt" sx={{ color: 'text.secondary' }}>
        {label}
      </Box>
      <Box
        component="dd"
        sx={{
          m: 0,
          textAlign: 'right',
          minWidth: 0,
          overflowWrap: 'anywhere',
          ...(numeric === true && { fontFamily: NUMERIC_FONT, fontVariantNumeric: 'tabular-nums' }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
